import React, { useCallback, useEffect } from "react";
import { RemoteParticipant as TwilioParticipant } from "twilio-video";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { useRemoteParticipant } from "./useRemoteParticipant";
import { Icon } from "components/shared/icon/Icon";
import styles from "./RemoteParticipant.module.css";
import { RemoteParticipantData } from "store/remote-participants/types";
import { useParticipantLayout } from "./useParticipantLayout";

interface RemoteParticipantProps {
  participant: TwilioParticipant;
  participantState: RemoteParticipantData;
  participantsCount?: number;
  inSidebar?: boolean;
  audioOnly?: boolean;
  isDominantSpeaker?: boolean;
}
export const RemoteParticipant: React.FC<RemoteParticipantProps> = ({
  participant,
  participantState,
  participantsCount,
  inSidebar,
  audioOnly,
  isDominantSpeaker,
}) => {
  const { muteAll } = useSelector((s: RootState) => s.remoteParticipants);
  const { videoRef, audioRef } = useRemoteParticipant(
    participant,
    participantState
  );
  const { audioOutput } = useSelector((s: RootState) => s.localTracks);
  const layout = useParticipantLayout(participantsCount || 0);

  useEffect(() => {
    if ((audioRef as any)?.current?.setSinkId) {
      const ref = audioRef.current;

      if (ref) {
        ref.addEventListener('loadeddata', (event: any) => {
          (ref as any).setSinkId(audioOutput?.deviceId);
        });

        if (ref.readyState > 2) {
          (ref as any).setSinkId(audioOutput?.deviceId);
        }
      }
    }
  }, [audioOutput, audioRef]);

  const showVideo = useCallback(() => {
    return (
      !audioOnly &&
      participantState?.videoIsEnabled &&
      !participantState?.isSwitchedOff
    );
  }, [participantState, audioOnly]);

  return (
    <>
      <div
        className={`${styles["participant"]} ${
          inSidebar ? styles["sidebar"] : styles[layout]
        }`}
      >
        <div
          className={`${styles["participant-on-container"]} ${
            showVideo() ? "" : styles["switched-off"]
          }`}
        >
          <div className={`${styles["participant-identity"]}`}>
            <span>{participant.identity}</span>
            {!participantState?.audioIsEnabled && (
              <Icon
                className={styles["muted-participant"]}
                id="microphone"
              ></Icon>
            )}
          </div>
          <div
            key={`${participantState.identity}-camera-vid-element`}
            className={`${styles["video-element"]} ${
              participantState.isSharingScreen ? styles["highlight"] : ""
            }`}
          >
            <video ref={videoRef} autoPlay={true} />
          </div>
        </div>
        {!showVideo() && inSidebar && (
          <div className={styles["participant-identity-large"]}>
            <span className={styles["icon-wrapper"]}>
              <Icon
                className={`${styles["user-icon"]} ${
                  isDominantSpeaker ? styles["dominant"] : ""
                }`}
                id="profile"
              ></Icon>
            </span>
            <span className={styles["identity-text"]}>
              {participant.identity}
            </span>
            <span className={styles["participant-state-icons"]}>
              {!participantState?.audioIsEnabled && (
                <span className={styles["icon-wrapper"]}>
                  <Icon
                    className={styles["muted-participant"]}
                    id="microphone"
                  ></Icon>
                </span>
              )}
              {(!participantState.videoIsEnabled ||
                participantState.isSwitchedOff) && (
                <span className={styles["icon-wrapper"]}>
                  <Icon
                    className={styles["hidden-participant"]}
                    id="video-camera"
                  ></Icon>
                </span>
              )}
            </span>
          </div>
        )}
      </div>
      <audio
        ref={audioRef}
        autoPlay={true}
        muted={muteAll || !participantState?.audioIsEnabled}
      />
    </>
  );
};
